import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Clock, ArrowRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import useReveal from '@/hooks/useReveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  useReveal();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const displayFont = isAr ? 'var(--font-ar-display)' : 'var(--font-en-display)';
  const bodyFont = isAr ? 'var(--font-ar-body)' : 'var(--font-en-body)';
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/blog/articles`).then(r => setArticles(r.data)).catch(() => {});
  }, []);

  const filtered = articles.filter(a => {
    const title = isAr ? a.title_ar : a.title_en;
    const excerpt = isAr ? a.excerpt_ar : a.excerpt_en;
    return (title + excerpt).toLowerCase().includes(search.toLowerCase());
  });

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="bg-[#071E2C] pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,168,76,0.15),transparent_60%)] pointer-events-none" />
        <div className="absolute inset-x-6 lg:inset-x-12 top-24 h-px bg-gradient-to-r from-transparent via-[#A68966]/30 to-transparent" />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 relative">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8">
            <div className="max-w-2xl">
              <div className="gmre-overline mb-5 gmre-reveal">{isAr ? 'المدونة الاستراتيجية' : 'Strategic Insights · Journal'}</div>
              <h1
                className="text-4xl sm:text-5xl lg:text-[56px] font-semibold text-white mb-5 tracking-tight gmre-reveal gmre-reveal-delay-1"
                style={{ fontFamily: displayFont, lineHeight: 1.1, textWrap: 'balance' }}
              >
                {isAr ? 'رؤى وتحليلات الاستثمار' : 'Investment Insights & Analysis'}
              </h1>
              <div className="gmre-ornament justify-start mb-6 gmre-reveal gmre-reveal-delay-2"><div className="line" style={{ width: 72 }} /><div className="dot" /></div>
              <p className="text-white/65 text-base lg:text-lg max-w-xl leading-relaxed gmre-reveal gmre-reveal-delay-3" style={{ fontFamily: bodyFont, lineHeight: isAr ? '2' : '1.75' }}>
                {isAr ? 'تحليلات حصرية واستراتيجيات استثمارية من خبراء جُليد لمساعدتك في اتخاذ قرارات مالية ذكية.' : 'Exclusive analysis and investment strategies from Guleed Mortgage experts to help you make smart financial decisions.'}
              </p>
            </div>
            <div className="w-full lg:w-96 gmre-reveal gmre-reveal-delay-4">
              <div className="gmre-glass rounded-full relative">
                <Search className="absolute start-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/55" />
                <Input
                  data-testid="blog-search"
                  placeholder={isAr ? 'ابحث في المقالات...' : 'Search articles…'}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="ps-11 h-12 bg-transparent border-0 text-white placeholder:text-white/45 rounded-full focus-visible:ring-1 focus-visible:ring-[#C9A84C]/40"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFB] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
          {/* Featured Article */}
          {featured && (
            <Link to={`/blog/${featured.slug}`} data-testid="featured-article" className="block mb-16 lg:mb-20 group gmre-reveal">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 gmre-glass-light rounded-3xl overflow-hidden">
                <div className="lg:col-span-3 h-72 lg:h-auto relative overflow-hidden">
                  <img src={featured.image} alt="" onError={(e) => { e.target.src = 'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=800'; }} className="w-full h-full object-cover group-hover:scale-[1.04] gmre-ios-transition" />
                  <div className="absolute top-5 start-5">
                    <span className="gmre-glass-pill text-[10px] tracking-[0.22em] uppercase text-white px-4 py-1.5">{isAr ? 'المقال المميّز' : 'Featured Story'}</span>
                  </div>
                </div>
                <div className="lg:col-span-2 p-10 lg:p-12 flex flex-col justify-center">
                  <Badge className="w-fit bg-[#A68966]/10 text-[#A68966] border border-[#A68966]/20 mb-5 text-[10px] tracking-[0.2em] uppercase rounded-full px-3 py-1">
                    {isAr ? featured.category_ar : featured.category_en}
                  </Badge>
                  <h2 className="text-2xl lg:text-3xl font-semibold text-[#0B2F44] mb-4 group-hover:text-[#A68966] transition-colors tracking-tight" style={{ fontFamily: displayFont, lineHeight: 1.2 }}>
                    {isAr ? featured.title_ar : featured.title_en}
                  </h2>
                  <p className="text-[#4A6572] text-sm mb-6 leading-relaxed" style={{ lineHeight: isAr ? '2' : '1.75' }}>
                    {isAr ? featured.excerpt_ar : featured.excerpt_en}
                  </p>
                  <div className="flex items-center gap-4 text-[10px] text-[#4A6572] tracking-wider uppercase">
                    <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {featured.read_time} min</span>
                    <span className="w-1 h-1 rounded-full bg-[#A68966]" />
                    <span>{featured.author}</span>
                    <span className="w-1 h-1 rounded-full bg-[#A68966]" />
                    <span>{featured.published_at}</span>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Section header */}
          {rest.length > 0 && (
            <div className="flex items-center justify-between mb-10 gmre-reveal">
              <div>
                <div className="gmre-overline mb-3" style={{ color: '#A68966' }}>{isAr ? 'كل المقالات' : 'All Articles'}</div>
                <h3 className="text-2xl lg:text-3xl font-semibold text-[#0B2F44] tracking-tight" style={{ fontFamily: displayFont }}>{isAr ? 'استكشف المعرفة' : 'Explore the Library'}</h3>
              </div>
              <div className="hidden sm:block gmre-glass-divider-light w-32" />
            </div>
          )}

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {rest.map((article, i) => (
              <Link
                key={i}
                to={`/blog/${article.slug}`}
                data-testid={`blog-card-${i}`}
                className={`gmre-glass-light rounded-3xl overflow-hidden group gmre-reveal gmre-reveal-delay-${Math.min((i % 3) + 1, 3)}`}
              >
                <div className="h-52 overflow-hidden relative">
                  <img src={article.image} alt="" onError={(e) => { e.target.src = 'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=800'; }} className="w-full h-full object-cover group-hover:scale-[1.06] gmre-ios-transition" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B2F44]/20 to-transparent opacity-0 group-hover:opacity-100 gmre-ios-transition" />
                </div>
                <div className="p-7">
                  <Badge className="bg-[#A68966]/10 text-[#A68966] border border-[#A68966]/20 mb-4 text-[10px] tracking-[0.18em] uppercase rounded-full px-3 py-1">
                    {isAr ? article.category_ar : article.category_en}
                  </Badge>
                  <h3 className="font-semibold text-[#0B2F44] mb-3 group-hover:text-[#A68966] transition-colors line-clamp-2 text-lg tracking-tight" style={{ fontFamily: displayFont, lineHeight: 1.3 }}>
                    {isAr ? article.title_ar : article.title_en}
                  </h3>
                  <p className="text-[#4A6572] text-sm mb-5 line-clamp-2" style={{ lineHeight: isAr ? '1.85' : '1.65' }}>
                    {isAr ? article.excerpt_ar : article.excerpt_en}
                  </p>
                  <div className="flex items-center justify-between text-[10px] text-[#4A6572] tracking-wider uppercase pt-4 border-t border-[#D0D8DC]/40">
                    <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {article.read_time} min</span>
                    <span className="text-[#A68966] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      {isAr ? 'اقرأ' : 'Read'} <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
